import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { Checkbox, FormControlLabel, Grid, Typography } from '@mui/material';
import useSWR from 'swr';
import { FormikProps } from 'formik';
import { PageWrapper } from '@shared/styledComponents';
import { getPeriodOptions } from '@shared/commonFunctions';
import { IPeriod } from '@shared/interfaces';
import { SERVICES } from '@shared/constants/apiConfig';
import { Notistack } from '@shared/components/Notistack/Notistack';
import { AlertModal } from '@shared/components/modals/AlertModal';
import { useConfirmationModal } from '@shared/hooks/useConfirmationModal';
import { SelectAutocomplete } from '@shared/inputs/SelectAutocomplete';
import { IChargeBack } from '../interfaces';
import { requestUpdateChargeBacks } from '../requests';
import { ChargeBacksTable } from '../components/ChargeBacksTable';

export const ChargeBacks = () => {
  const formikRef = useRef<FormikProps<IChargeBack[]>>(null);
  const { isOpen, onCancel, onConfirm, openDialog } = useConfirmationModal();
  const [periodId, setPeriodId] = useState<string | null>(null);
  const [showCurrentLC, setShowCurrentLC] = useState(true);

  const { data: periods = [] } = useSWR<IPeriod[]>(`${SERVICES.USER}/period`);
  const { data: chargeBacks = [] } = useSWR<IChargeBack[]>(periodId && `${SERVICES.USER}/users/chargebacks/${periodId}/${showCurrentLC}`);

  // Add ID for Editing mode
  const chargeBacksRows = useMemo(
    () => chargeBacks.map((chargeBack, index) => ({ id: chargeBack.userId, index, ...chargeBack })),
    [chargeBacks]
  );

  // Set current date as period
  useEffect(
    () => {
      if (periods.length) {
        const currentDate = new Date();
        const currentMonth = currentDate.getMonth();
        const currentYear = currentDate.getFullYear();

        const currentPeriod = periods.find(period => period.year === currentYear && moment(period.fromDate).month() === currentMonth);

        if (currentPeriod) {
          setPeriodId(currentPeriod.id);
        }
      }
    },
    [periods]
  );

  const periodOptions = useMemo(() => getPeriodOptions(periods), [periods]);

  const onSaveChanges = useCallback(
    async(values: IChargeBack[]) => {
      if (periodId) {
        await requestUpdateChargeBacks(periodId, values);
        formikRef.current?.resetForm({ values });
      } else {
        Notistack.generalError('Please select period');
      }
    },
    [periodId]
  );

  const onCancelChanges = useCallback(
    async() => {
      const result = await openDialog();

      if (result) {
        formikRef.current?.handleReset();
      }
    },
    []
  );

  const handleChangePeriod = useCallback(
    async(value: unknown) => {
      if (formikRef.current?.dirty) {
        const result = await openDialog();

        if (result) {
          setPeriodId(value as string);
        }
      } else {
        setPeriodId(value as string);
      }
    },
    [periodId]
  );

  return (
    <PageWrapper>
      <AlertModal 
        open={isOpen}
        title="Unsaved Changes"
        subtitle="Are you sure you want to cancel your changes?"
        onClose={onCancel}
        onConfirm={onConfirm}
      />

      <Grid container spacing={3}>
        <Grid item md={2} xs={12}>
          <SelectAutocomplete
            label="Date Filter"
            sx={{ minWidth: 200 }}
            value={periodId}
            options={periodOptions}
            onChange={handleChangePeriod}
          />
        </Grid>
        <Grid item>
          <FormControlLabel
            name="showCurrentLC"
            label={<Typography>Only show current LCs</Typography>}
            control={
              <Checkbox
                checked={showCurrentLC}
                onChange={(_, checked) => setShowCurrentLC(checked)}
              />}
          />
        </Grid>
      </Grid>

      <ChargeBacksTable
        formikRef={formikRef}
        rows={chargeBacksRows}
        onSaveChanges={onSaveChanges}
        onCancelChanges={onCancelChanges}
      />
    </PageWrapper>
  );
};
